import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Download, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRailItems } from "@/hooks/agent/useRailItems";
import type { RailItem, ResourcesRailProps } from "@/types/voice/resources-rail";
import { LINK_ICON_MAP } from "./infoLinkIcons";

/**
 * The always-visible resources rail.
 *
 * Documents and next steps used to live behind two 36px icons and a post-call
 * panel, which meant nobody who didn't already know a manual existed ever
 * found one. The rail shows the items themselves instead of a door: a named
 * chip ("Installation manual · PDF · 2.4 MB") is a thing a visitor wants, a
 * paperclip is not.
 *
 * It never unmounts. Desktop docks it vertically in the left gutter — the
 * right column already belongs to the transcript — and mobile lays the same
 * component down as a horizontal band between the genie and the chat drawer.
 * Only the density changes across the conversation, so it reads as furniture
 * rather than something that popped up.
 */
export const ResourcesRail = ({
  resources,
  brandColor,
  variant = "vertical",
  compact = false,
  surfacedId = null,
  onOpenAll,
  onSelect,
  onImpression,
  maxItems,
}: ResourcesRailProps) => {
  const allItems = useRailItems(resources);
  const reduceMotion = useReducedMotion();
  const isVertical = variant === "vertical";
  const limit = maxItems ?? (isVertical ? 3 : 2);

  // The surfaced item is pinned first, so a Genie that just named the manual
  // puts the manual under the visitor's eye without them hunting for it.
  const items = useMemo(() => {
    if (!surfacedId) return allItems.slice(0, limit);
    const surfaced = allItems.find((item) => item.id === surfacedId);
    if (!surfaced) return allItems.slice(0, limit);
    return [surfaced, ...allItems.filter((item) => item.id !== surfacedId)].slice(0, limit);
  }, [allItems, surfacedId, limit]);

  // Impressions are logged once per resource per session, so the click-through
  // numbers have a denominator. Without this we still wouldn't know whether
  // anyone ever sees these.
  const seenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!onImpression) return;
    items.forEach((item) => {
      if (seenRef.current.has(item.id)) return;
      seenRef.current.add(item.id);
      onImpression(item);
    });
  }, [items, onImpression]);

  if (allItems.length === 0) return null;

  const overflow = allItems.length - items.length;

  return (
    <div
      className={cn("flex w-full flex-col", compact ? "gap-1.5" : "gap-2")}
      data-testid="resources-rail"
    >
      {!compact && (
        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#9AA7B6]">
          Take these with you
        </span>
      )}

      <div
        className={cn(
          "flex",
          isVertical
            ? "flex-col gap-2"
            : "flex-row gap-2 overflow-x-auto pb-0.5 [mask-image:linear-gradient(to_right,#000_78%,transparent_100%)]",
        )}
      >
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <RailChip
              key={item.id}
              item={item}
              brandColor={brandColor}
              compact={compact}
              lit={item.id === surfacedId}
              fixedWidth={!isVertical}
              reduceMotion={!!reduceMotion}
              onSelect={onSelect}
            />
          ))}
        </AnimatePresence>
      </div>

      {overflow > 0 && onOpenAll && (
        <button
          type="button"
          onClick={onOpenAll}
          className="self-start rounded text-xs font-medium underline-offset-2 transition-opacity hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ color: brandColor, outlineColor: brandColor }}
        >
          All {allItems.length} resources
        </button>
      )}
    </div>
  );
};

interface RailChipProps {
  item: RailItem;
  brandColor: string;
  compact: boolean;
  lit: boolean;
  fixedWidth: boolean;
  reduceMotion: boolean;
  onSelect?: (item: RailItem) => void;
}

const RailChip = ({
  item,
  brandColor,
  compact,
  lit,
  fixedWidth,
  reduceMotion,
  onSelect,
}: RailChipProps) => {
  const isDocument = item.kind === "document";
  const LinkIcon = LINK_ICON_MAP[item.icon ?? ""] ?? LINK_ICON_MAP.link;
  const showMeta = !compact && !lit && !!item.meta;

  return (
    <motion.a
      layout={!reduceMotion}
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      download={isDocument ? item.fileName || undefined : undefined}
      onClick={() => onSelect?.(item)}
      initial={reduceMotion ? false : { opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      aria-label={
        isDocument ? `Download ${item.label}${item.meta ? `, ${item.meta}` : ""}` : `Open ${item.label}`
      }
      className={cn(
        "group flex items-center rounded-xl border bg-white transition-shadow",
        compact ? "gap-2.5 p-2" : "gap-3 p-2.5",
        fixedWidth ? "min-w-[148px] flex-none" : "w-full",
        "hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.28)]",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        lit ? "border-transparent" : "border-[#E9EDF3]",
      )}
      style={{
        outlineColor: brandColor,
        ...(lit
          ? {
              backgroundColor: `${brandColor}0A`,
              boxShadow: `0 0 0 2px ${brandColor}`,
            }
          : {}),
      }}
    >
      <span
        className={cn(
          "flex flex-none items-center justify-center rounded-lg",
          compact ? "h-7 w-7" : "h-9 w-9",
        )}
        style={{ backgroundColor: `${brandColor}14`, color: brandColor }}
      >
        {isDocument ? (
          <FileText size={compact ? 14 : 17} strokeWidth={2} />
        ) : (
          <LinkIcon size={compact ? 14 : 17} />
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[13px] font-medium text-[#222B45]">{item.label}</span>
        {showMeta && <span className="truncate text-[11px] text-[#9AA7B6]">{item.meta}</span>}
        {lit && (
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.07em]"
            style={{ color: brandColor }}
          >
            Just mentioned
          </span>
        )}
      </span>

      {!compact && (
        <span className="flex flex-none items-center justify-center" style={{ color: brandColor }} aria-hidden>
          {isDocument ? <Download size={15} strokeWidth={2.25} /> : <ArrowUpRight size={15} strokeWidth={2.25} />}
        </span>
      )}
    </motion.a>
  );
};

export default ResourcesRail;
